/**
 * Date utilities for arc components
 * Provides offset parsing, date arithmetic, and relative time formatting
 */

export interface OffsetParsed {
  value: number;
  unit: 'd' | 'w' | 'm' | 'y';
}

/**
 * Parse offset string like "+7d", "-3w", "2m"
 * @param offset - Offset string (e.g., "+7d", "-3w", "2m", "1y")
 * @returns Parsed offset with value and unit
 * @throws Error if offset format is invalid
 * @example
 * parseOffset('+7d') // { value: 7, unit: 'd' }
 * parseOffset('-3w') // { value: -3, unit: 'w' }
 */
export function parseOffset(offset: string): OffsetParsed {
  const regex = /^([+-]?)(\d+)([dwmy])$/;
  const match = regex.exec(offset);
  if (!match) {
    throw new Error(`Invalid offset format: ${offset}. Expected format: [+/-]<number><d|w|m|y>`);
  }
  const [, sign, num, unit] = match;
  const value = Number.parseInt(num, 10) * (sign === '-' ? -1 : 1);
  return { value, unit: unit as 'd' | 'w' | 'm' | 'y' };
}

/**
 * Add offset to date
 * @param date - Base date
 * @param offset - Offset string (e.g., "+7d", "-3w", "2m", "1y")
 * @returns New date with offset applied
 * @example
 * addOffset(new Date('2025-01-15'), '+7d') // 2025-01-22
 * addOffset(new Date('2025-01-15'), '-2w') // 2025-01-01
 */
export function addOffset(date: Date, offset: string): Date {
  const { value, unit } = parseOffset(offset);
  const result = new Date(date);

  switch (unit) {
    case 'd':
      result.setDate(result.getDate() + value);
      break;
    case 'w':
      result.setDate(result.getDate() + value * 7);
      break;
    case 'm':
      result.setMonth(result.getMonth() + value);
      break;
    case 'y':
      result.setFullYear(result.getFullYear() + value);
      break;
  }

  return result;
}

/**
 * Calculate days between two dates
 * @param from - Start date
 * @param to - End date
 * @returns Number of days between dates (positive if to > from, negative if from > to)
 * @example
 * daysBetween(new Date('2025-01-15'), new Date('2025-01-20')) // 5
 * daysBetween(new Date('2025-01-20'), new Date('2025-01-15')) // -5
 */
export function daysBetween(from: Date, to: Date): number {
  const msPerDay = 1000 * 60 * 60 * 24;
  const utcFrom = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const utcTo = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.floor((utcTo - utcFrom) / msPerDay);
}

/**
 * Format relative time (e.g., "2 days ago", "in 3 hours")
 * Uses Intl.RelativeTimeFormat for localized output
 * @param date - Date to format
 * @param locale - Locale for formatting (default: 'en-US')
 * @returns Formatted relative time string
 * @example
 * formatRelativeTime(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)) // "2 days ago"
 * formatRelativeTime(new Date(Date.now() + 3 * 60 * 60 * 1000)) // "in 3 hours"
 */
export function formatRelativeTime(date: Date, locale = 'en-US'): string {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHours = Math.round(diffMin / 60);
  const diffDays = Math.round(diffHours / 24);

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Math.abs(diffDays) >= 1) {
    return rtf.format(diffDays, 'day');
  } else if (Math.abs(diffHours) >= 1) {
    return rtf.format(diffHours, 'hour');
  } else if (Math.abs(diffMin) >= 1) {
    return rtf.format(diffMin, 'minute');
  } else {
    return rtf.format(diffSec, 'second');
  }
}

/**
 * Format duration in human-readable form
 * @param days - Number of days
 * @returns Formatted duration string
 * @example
 * formatDuration(1) // "1 day"
 * formatDuration(7) // "1 week"
 * formatDuration(45) // "1 month, 2 weeks"
 */
export function formatDuration(days: number): string {
  if (days === 0) return '0 days';

  const absDays = Math.abs(days);
  const sign = days < 0 ? '-' : '';

  const years = Math.floor(absDays / 365);
  const months = Math.floor((absDays % 365) / 30);
  const weeks = Math.floor((absDays % 30) / 7);
  const remainingDays = absDays % 7;

  const parts: string[] = [];

  if (years > 0) parts.push(`${years} year${years === 1 ? '' : 's'}`);
  if (months > 0) parts.push(`${months} month${months === 1 ? '' : 's'}`);
  if (weeks > 0) parts.push(`${weeks} week${weeks === 1 ? '' : 's'}`);
  if (remainingDays > 0) parts.push(`${remainingDays} day${remainingDays === 1 ? '' : 's'}`);

  return sign + parts.join(', ');
}
